import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { STORAGE_KEY, translations } from './translations';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'ar' || stored === 'en') return stored;
    } catch {
      /* ignore */
    }
    return 'en';
  });

  const setLanguage = useCallback((next) => {
    setLanguageState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  }, [language, setLanguage]);

  const messages = translations[language];

  const t = useCallback(
    (path) => {
      const keys = path.split('.');
      let v = messages;
      for (const k of keys) {
        v = v?.[k];
      }
      return typeof v === 'string' ? v : path;
    },
    [messages]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.lang = language === 'ar' ? 'ar' : 'en';
    root.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.title = messages.meta.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', messages.meta.description);
  }, [language, messages.meta]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      toggleLanguage,
      messages,
      t,
      isRTL: language === 'ar',
    }),
    [language, setLanguage, toggleLanguage, messages, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
